import { queryOptions, useQuery } from '@tanstack/react-query';

import { getApiClient } from '@cast/api-client';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import type { ReportingMonetizationReadinessResponse } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { REPORTING_TRIAL_ENABLED } from './reportingTrialEnabled';

const REPORTING_MONETIZATION_READINESS_QUERY_KEY =
  'reporting-monetization-readiness';

export const getReportingMonetizationReadinessQueryKey = (
  organizationId?: string
) => [REPORTING_MONETIZATION_READINESS_QUERY_KEY, organizationId] as const;

const getReportingMonetizationReadinessQueryOptions = (
  organizationId: string | undefined,
  enabled: boolean
) =>
  queryOptions({
    queryKey: getReportingMonetizationReadinessQueryKey(organizationId),
    queryFn: async (): Promise<ReportingMonetizationReadinessResponse> => {
      const { data } =
        await getApiClient().costReport.getReportingMonetizationReadiness();
      return data;
    },
    enabled: makeIsEnabled(enabled, !!organizationId, REPORTING_TRIAL_ENABLED),
  });

/**
 * Org-level activation checklist signals from
 * GET /v1/cost-reports/reporting-monetization/readiness.
 *
 * Maps to Getting Started ticks: connected cluster, allocation groups,
 * budgets, OpsPilot question asked. OpsPilot may lag (PostHog on BE).
 */
export const useReportingMonetizationReadinessQuery = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) => {
  const { isReady: isOrganizationReady, currentOrganization } =
    useOrganizations();

  return useQuery(
    getReportingMonetizationReadinessQueryOptions(
      currentOrganization?.id,
      enabled && isOrganizationReady
    )
  );
};
